<template>
  <form
    v-if="showForm"
    id="articleForm"
    action
    method="POST"
  >
    <h1
      class="title is-3"
      v-t="'title_' + formMode"
    />

    <b-field>
      <b-checkbox
        true-value="1"
        false-value="0"
        size="is-medium"
        type="is-danger"
        v-model="formData.advanced"
      >
        <span v-t="'advanced_article'" />
      </b-checkbox>
    </b-field>

    <b-field
      :label="$t('fields.labels.title')"
      :type="{ 'is-danger': errors.title }"
      :message="errors.title ? errors.title[0] : ''"
      :custom-class="errors.title ? 'has-text-danger' : ''"
    >
      <b-input
        type="text"
        :placeholder="$t('fields.placeholders.title')"
        :value="formData.title"
        v-model="formData.title"
        size="is-large"
        required
      />
    </b-field>

    <b-field
      :label="$t('fields.labels.description')"
      :type="{ 'is-danger': errors.description }"
      :message="errors.description ? errors.description[0] : ''"
      :custom-class="errors.description ? 'has-text-danger' : ''"
    >
      <b-input
        type="text"
        :placeholder="$t('fields.placeholders.description')"
        :value="formData.description"
        v-model="formData.description"
        size="is-medium"
        required
      />
    </b-field>

    <b-field
      :label="$t('fields.labels.content')"
      :type="{ 'is-danger': errors.content }"
      :message="errors.content ? errors.content[0] : ''"
      :custom-class="errors.content ? 'has-text-danger' : ''"
    >
      <editor
       api-key="6xnyjatw6m7qohouudzphyp5ua5jjhle5bf0zxu7coxwi1fw"
       :initial-value="formData.content"
       @onChange="contentChange1"
       :init="{
         height: 500,
         menubar: false,
         plugins: [
           'advlist autolink lists link image charmap print preview anchor',
           'searchreplace visualblocks code fullscreen',
           'insertdatetime media table paste code help wordcount',
           'print preview powerpaste casechange importcss searchreplace autolink autosave save directionality advcode',
           'visualblocks visualchars fullscreen image link media mediaembed template codesample table charmap hr pagebreak nonbreaking',
           'anchor toc insertdatetime advlist lists checklist wordcount tinymcespellchecker a11ychecker imagetools textpattern noneditable',
           'help formatpainter permanentpen pageembed charmap mentions quickbars linkchecker emoticons advtable'
         ],
         toolbar:
           'undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat | pagebreak | charmap emoticons | fullscreen  preview save print | insertfile image media pageembed template link anchor codesample | a11ycheck ltr rtl | showcomments addcomment'
       }"
      />
    </b-field>

    <button
      type="submit"
      class="button is-danger"
      @click.prevent="save()"
    >
      <b-icon icon="save" />
      <span v-t="'save'" />
    </button>
  </form>
  <loading-content v-else />
</template>

<script>
import API from '@/api'
import { mapMutations } from 'vuex'
import RichEditor from './RichEditor'
import LoadingContent from './LoadingContent'
import Editor from '@tinymce/tinymce-vue'

export default {
  name: 'ArticleForm',
  props: { product: { type: Object, required: true } },
  components: {
    RichEditor,
    Editor,
    LoadingContent
  },
  data () {
    return {
      isLoading: true,
      errors: {},
      formData: {
        product_id: this.product.id,
        title: '',
        description: '',
        content: '',
        advanced: false,
        attachs: []
      }
    }
  },

  mounted () {
    if (this.formMode === 'edit') {
      this.fetchFormData()
    } else {
      this.isLoading = false
    }
  },

  methods: {
    fetchFormData () {
      this.isLoading = true
      API.articles.find(this.articleId, article => {
        this.isLoading = false
        this.formData = {
          product_id: article.product.id,
          title: article.title,
          description: article.description,
          content: article.content,
          advanced: article.advanced ? '1' : '0'
        }
      })
    },

    contentChange1 (api, changeData) {
      document.jao = changeData
      this.formData.content = changeData.getContent()
      this.formData.attachs = ''
    },

    contentChange ({ content, attachs }) {
      console.log('teste')
      this.formData.content = content
      this.formData.attachs = attachs
    },

    save () {
      if (this.formMode === 'new') {
        API.articles.store(
          this.formData,
          article => {
            this.saveCallback(article)
            this.addArticleToIndex(article)
          },
          this.errorCallback
        )
      } else {
        API.articles.update(this.articleId, this.formData, this.saveCallback, this.errorCallback)
      }
    },

    saveCallback (article) {
      this.$snotify.success(this.$t('alerts.' + this.formMode), this.$t('alert.title.success'))
      this.$router.push({ name: 'article', params: { article: article.slug } })
      window.scrollTo(0, 0)
    },

    errorCallback (err) {
      this.$snotify.error(this.$t('alerts.error'), this.$t('alert.title.error'))
      if (err.response) {
        this.errors = err.response.data.errors
      }
    },

    ...mapMutations({ addArticleToIndex: 'article/addArticle' })
  },

  computed: {
    articleId () {
      return this.$route.params.article
    },

    showForm () {
      return this.formMode === 'new' || (this.formMode === 'edit' && !this.isLoading)
    },

    formMode () {
      return this.$route.name === 'new-article' ? 'new' : 'edit'
    }
  },

  metaInfo () {
    return { title: this.$t(this.formMode === 'new' ? 'title_new' : 'title_edit') }
  }
}
</script>

<i18n src="@/lang/locales.json"></i18n>
<i18n>
{
  "en-US": {
    "title_new": "Create new article",
    "title_edit": "Editing the article",
    "fields": {
      "labels": {
        "title": "Title",
        "description": "Subtitle",
        "content": "Content"
      },
      "placeholders": {
        "title": "Article title",
        "description": "A short description",
        "content": "The article content"
      }
    },
    "advanced_article": "Restricted content / Not visible to customers",
    "save": "Save informations",
    "alerts": {
      "new": "Article created successfully!",
      "edit": "Article updated successfully!",
      "error": "An error occurred when creating the article!"
    }
  },
  "pt-BR": {
    "title_new": "Escrever novo artigo",
    "title_edit": "Editando o artigo",
    "fields": {
      "labels": {
        "title": "Título",
        "description": "Subtítulo",
        "content": "Conteúdo"
      },
      "placeholders": {
        "title": "Título do artigo",
        "description": "Uma breve descrição",
        "content": "Conteúdo do artigo"
      }
    },
    "advanced_article": "Conteúdo restrito / Não visível para clientes",
    "save": "Salvar informações",
    "alerts": {
      "new": "Artigo cadastrado com sucesso!",
      "edit": "Artigo atualizado com sucesso!",
      "error": "Erro ao cadastrar o artigo!"
    }
  }
}
</i18n>
